import { Card, Classes } from "@blueprintjs/core";
import { ButtonsSection, FGCustomPanel, FGTextInput, showError } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import styled from "styled-components";
import * as Yup from "yup";

import { AuthenticateCommand, EPlatform, ErrorDetails, ESRSHExceptions, WebUserApi } from "../../../../api";
import { PageContainer } from "../../../../components";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { EDrupalRoutes } from "../../../EDrupalRoutes";
import { EDrupalMailError } from "./DrupalEmailErrorPage";

export interface IDrupalRegisterPageProps {}

const StyledCard = styled(Card)`
  width: 40%;
  & .${Classes.FORM_GROUP} {
    margin: 0 0 15px !important;
  }
`;

export const DrupalRegisterPage: React.FunctionComponent<IDrupalRegisterPageProps> = () => {
  const { t } = useTl();
  const api = useApiService(WebUserApi);
  const history = useHistory();
  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      email: Yup.string().nullable().email(t(ETLCodes.InvalidEmail)).required(t(ETLCodes.Login_EmailIsRequired)),
    });
  }, [t]);

  const checkMail = React.useCallback(
    async (mail: string) => {
      try {
        await api.checkEmail(mail);
        history.push(`${EDrupalRoutes.emailError}/${EDrupalMailError[EDrupalMailError.AccountExists]}`);
      } catch (error) {
        const details: ErrorDetails = JSON.parse(error.response);
        if (details?.errorCode) {
          switch (details?.errorCode) {
            case ESRSHExceptions.UserDoesNotExist: {
              history.push(`${EDrupalRoutes.emailError}/${EDrupalMailError[EDrupalMailError.NotExist]}`);
              break;
            }
            case ESRSHExceptions.MultipleMatchingEmail: {
              history.push(`${EDrupalRoutes.emailError}/${EDrupalMailError[EDrupalMailError.MultipleMatch]}`);
              break;
            }
            case ESRSHExceptions.UserHasNoPortalAccount: {
              history.push(`${EDrupalRoutes.emailError}/${EDrupalMailError[EDrupalMailError.NoWebUser]}/${mail}`);
              break;
            }
            default: {
              showError(t(ETLCodes.Error));
            }
          }
        }
      }
    },
    [api, history, t]
  );

  return (
    <PageContainer isCentered={true}>
      <h1>{t(ETLCodes.DrupalCheckAccount)}</h1>
      <StyledCard elevation={2}>
        <h2>{t(ETLCodes.EnterYourData)}</h2>
        <DrupalFormGenerator
          initialValues={AuthenticateCommand.fromJS({
            login: "",
            password: "",
            rememberMe: false,
            platform: EPlatform.PORTAL,
          })}
          onSubmit={(d) => {
            checkMail(d.email);
          }}
          labelAlignment="right"
          fill={true}
          inline={true}
          boldLabel={false}
          validationSchema={validationSchema}
          hideButtons={true}
          autoRequiredMark={true}
          showColons={false}
          enableDirtyCheck={false}
          large={true}
        >
          <FGTextInput name="email" label={t(ETLCodes.MailAddress)} />
          <FGCustomPanel>
            {() => (
              <ButtonsSection
                buttons={[
                  {
                    outlined: true,
                    minimal: true,
                    position: "center",
                    text: t(ETLCodes.DrupalGetUser),
                    intent: "primary",
                    type: "submit",
                    large: true,
                  },
                ]}
              />
            )}
          </FGCustomPanel>
        </DrupalFormGenerator>
      </StyledCard>
    </PageContainer>
  );
};
